/**
 * Syncs document title and robots with the active route.
 *
 * Title template: `{Page} | FlahaSOIL | Flaha Agri Tech`
 * Indexation: only INDEXABLE_PATHS (see theme/seo.ts) — ADR 0001.
 *
 * Baseline metadata (OG, JSON-LD, canonical, icons) lives in index.html.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
	buildDocumentTitle,
	resolveRobotsForPath,
	SEO_DEFAULT_DESCRIPTION,
	SEO_OG_DESCRIPTION,
	SEO_OG_TITLE,
} from "../../theme/seo";
import { usePageHeaderState } from "../PageHeaderContext";

function setMetaContent(
	name: string,
	content: string,
	attr: "name" | "property" = "name"
) {
	const selector = `meta[${attr}="${name}"]`;
	let el = document.head.querySelector(selector) as HTMLMetaElement | null;
	if (!el) {
		el = document.createElement("meta");
		el.setAttribute(attr, name);
		document.head.appendChild(el);
	}
	el.setAttribute("content", content);
}

export function DocumentMeta() {
	const header = usePageHeaderState();
	const { pathname } = useLocation();

	useEffect(() => {
		const robots = resolveRobotsForPath(pathname);
		document.title = buildDocumentTitle(header.title);
		setMetaContent("robots", robots.content);

		// Keep a stable public description on indexable routes.
		if (robots.index) {
			setMetaContent("description", SEO_DEFAULT_DESCRIPTION);
			// Do not thrash OG titles on every in-app navigation for private pages.
			if (pathname === "/" || pathname === "") {
				setMetaContent("og:title", SEO_OG_TITLE, "property");
				setMetaContent("og:description", SEO_OG_DESCRIPTION, "property");
				setMetaContent("twitter:title", SEO_OG_TITLE);
				setMetaContent("twitter:description", SEO_OG_DESCRIPTION);
			}
		}
	}, [header.title, pathname]);

	return null;
}
